/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Accordion, AccordionHeader, AccordionPanel, Tag } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { SuccessFilledIcon } from '@coursera/cds-icons';

import CourseCard from 'bundles/degree-home/components/v3-dpm/components/CourseCard/index';
import type { UseCurriculum_SegmentFragment as GqlSegment } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import { getCreditsCompletionText } from 'bundles/degree-home/components/v3-dpm/lib/completionUtils';
import useProgressionCalculator from 'bundles/degree-home/components/v3-dpm/lib/useProgressionCalculator';
import { Card } from 'bundles/program-loggedin-shared/components/Card';
import { joinMetadataStrings } from 'bundles/program-loggedin-shared/components/Metadata';
import { useSegmentCompletionRule } from 'bundles/program-loggedin-shared/hooks/completionRules';
import { segmentCoursesAreElectiveOrRequired } from 'bundles/program-loggedin-shared/libraries/courseUtils';

import _t from 'i18n!nls/degree-home';

const styles = {
  segmentCard: (theme: Theme) => css`
    padding: ${theme.spacing(8)};
    margin-bottom: 0;

    ${theme.breakpoints.down('xs')} {
      padding: 0;
      border-radius: ${theme.spacing(4)};
      border: 2px solid ${theme.palette.gray[300]};
    }

    // Override for global CUI styles. For more context: https://coursera.slack.com/archives/CPJET5H4L/p1690558120578449
    button:focus {
      outline: none;
    }
  `,
  courseCardContainer: (theme: Theme) => css`
    border-top: 1px solid ${theme.palette.gray[300]};
    padding: 0 ${theme.spacing(8)};
  `,
  courseGrid: (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
  `,
};

interface Props {
  segment: GqlSegment;
}
const Segment: React.FC<Props> = ({ segment }) => {
  const track = useRetracked();
  const { title, courses, description, completionRule } = segment;
  const { minUnits } = completionRule;
  const overallProgression = useProgressionCalculator();
  const segmentProgression = overallProgression?.segments?.[segment.id];
  const completedCredits = segmentProgression?.summary?.completed;
  const inProgressCredits = segmentProgression?.summary?.inProgress;
  const isSegmentCompleted = segmentProgression?.completionRuleMet;

  const completionRuleText = useSegmentCompletionRule(minUnits);

  const creditsCompletionTextList = getCreditsCompletionText(completedCredits, inProgressCredits);
  const supportTextList = [completionRuleText, ...creditsCompletionTextList];

  const headerSupportText = joinMetadataStrings(supportTextList);

  // Don't need to handle 'unknown' case because completionRule is always present
  const allCoursesAreElective = segmentCoursesAreElectiveOrRequired(segment) === 'elective';

  const handleChange = (event: React.ChangeEvent<unknown>, expanded: boolean) => {
    if (expanded) {
      track({
        action: 'click',
        trackingName: 'segment_accordion',
        trackingData: {
          segment: {
            title,
            creditsRequired: minUnits,
            completedCredits,
            inProgressCredits,
          },
        },
      });
    }
  };

  return (
    <Card css={styles.segmentCard}>
      <Accordion variant="silent" onChange={handleChange}>
        <AccordionHeader
          label={title || ''}
          supportText={
            <div>
              <div>{headerSupportText}</div>
              {description && <div>{description}</div>}
            </div>
          }
        >
          {isSegmentCompleted && (
            <Tag
              data-testid="complete-tag-segment-level"
              variant="status"
              priority="primary"
              color="success"
              icon={<SuccessFilledIcon />}
            >
              {_t('Complete')}
            </Tag>
          )}
        </AccordionHeader>
        <AccordionPanel>
          <div css={styles.courseGrid}>
            {courses.map((course) => (
              <div
                key={`${course.segmentCourse.course.courseCode}${course.segmentCourse.course.courseName}`}
                css={styles.courseCardContainer}
              >
                <CourseCard course={course} isElective={allCoursesAreElective} />
              </div>
            ))}
          </div>
        </AccordionPanel>
      </Accordion>
    </Card>
  );
};

export default Segment;
